import {
  createDeleteListFormData,
  createCreateListFormData,
  createAddItemFormData,
  createAddToCartListFormData,
  createUpdateListFormData,
  listActionStandard,
  openOptionalModal,
} from './helper';
import { messageBoxSuccess, messageBoxError } from '../components/message-box';

class GroupEditor {
  public selectedElements: any = [];

  public groupTeasers: NodeListOf<HTMLElement>;

  public groupTeaserTrigger: NodeListOf<HTMLElement>;

  public groupEditor: HTMLElement;

  public groupEditorAmount: HTMLElement;

  public groupEditorSelectAll: HTMLElement;

  public actionButtons: NodeListOf<HTMLButtonElement>;

  public confirmPopup: HTMLElement;

  public addToCartButtons: NodeListOf<HTMLElement>;

  public wishlistContent: any;

  public cartIconHeader: HTMLElement | null;

  public cartIconHeaderInner: SVGTSpanElement | null | undefined;

  public lastScroll: number = 0;

  constructor(wishlistContent: any) {
    this.wishlistContent = wishlistContent;
  }

  updateEditor() {
    this.groupEditorAmount.innerHTML = this.selectedElements.length;

    if (this.selectedElements.find((element: any) => element.id == this.groupTeasers[0].dataset.listId)) {
      this.actionButtons.forEach((button) => {
        if (!button.classList.contains('js-add-to-cart')) {
          button.disabled = true;
        }
      })
    } else {
      this.actionButtons.forEach((button) => {
        if (!button.classList.contains('js-add-to-cart')) {
          button.disabled = false;
        }
      })
    }
  }

  handleToggleSelection(e: Event) {
    const groupTeaser = (e.currentTarget as HTMLElement).closest('.m-wishlist-group-teaser') as HTMLElement;

    if (groupTeaser.classList.contains('is-selected')) {
      groupTeaser.classList.remove('is-selected');
      this.selectedElements = this.selectedElements.filter((element: any) => element.id !== groupTeaser.dataset.listId);
    } else {
      groupTeaser.classList.add('is-selected');
      this.selectedElements.push({
        id: groupTeaser.dataset.listId,
        title: groupTeaser.dataset.listTitle,
      });
    }

    if (!this.selectedElements.length) {
      this.groupEditor.classList.remove('is-active');
    } else if (!this.groupEditor.classList.contains('is-active')) {
      this.groupEditor.classList.add('is-active');
      setTimeout(() => {
        this.groupEditor.classList.add('visible');
      }, 100);
    }

    this.updateEditor();
  }

  handleToggleSelectAll() {
    if (this.selectedElements.length === this.groupTeasers.length) {
      this.groupTeasers.forEach((teaser) => {
        teaser.classList.remove('is-selected');
      });

      this.groupEditor.classList.remove('is-active');
      this.selectedElements = [];
    } else {
      this.selectedElements = [];

      this.groupTeasers.forEach((teaser) => {
        teaser.classList.add('is-selected');
        this.selectedElements.push({
          id: teaser.dataset.listId,
          title: teaser.dataset.listTitle,
        });
      })
    }

    this.updateEditor();
  }

  async handleActionButton(e: Event) {
    const target = e.currentTarget as HTMLElement;

    switch(target.dataset.action) {
      case 'clone':
        const language: string | null = document.documentElement.lang ?? '';

        let groups: any = [];

        const requests = [];

        try {
          for (const element of this.selectedElements) {
            groups.push(`${element.title} - ${language === 'en-US' ? 'Copy' : 'Kopie'}`);

            const res = await listActionStandard(createCreateListFormData(`${element.title} - ${language === 'en-US' ? 'Copy' : 'Kopie'}`));
            const listToClone = this.wishlistContent.filter((list: any) => list.list_id == element.id)[0];

            listToClone.items?.forEach((item: any) => {
              const newItemFormData = createAddItemFormData(item.element_id, res.id);
              newItemFormData.append('fields[plaintext_title_1]', item.quantity);
              newItemFormData.append('fields[plaintext_title_2]', item.hide_price ? '1' : '0');

              requests.push(listActionStandard(newItemFormData));
            });

            let newSections: Array<any> = [];

            for (const section of listToClone.sections) {
              const newListFormData = createCreateListFormData(`${section.title} - ${language === 'en-US' ? 'Copy' : 'Kopie'}`, 'wishlist_section');
              newListFormData.append('fields[json_relation]', res.id);

              const sectionRes = await listActionStandard(newListFormData);

              newSections.push(sectionRes.id);

              for (const item of section.items) {
                const newItemFormData = createAddItemFormData(item.element_id, sectionRes.id);
                newItemFormData.append('fields[plaintext_title_1]', item.quantity);
                newItemFormData.append('fields[plaintext_title_2]', item.hide_price ? '1' : '0');

                requests.push(listActionStandard(newItemFormData));
              }
            }

            const updateParentListFormData = createUpdateListFormData(res.id);
            updateParentListFormData.append('fields[json_relation]', JSON.stringify({children: newSections}));

            requests.push(listActionStandard(updateParentListFormData));
          }

          await Promise.all(requests);

          messageBoxSuccess({
            contentDe: `Gruppe(n) wurde(n) erfolgreich dupliziert und als „${groups.join(', ')}“ angelegt.`,
            contentEn: `Successfully cloned group(s) as „${groups.join(', ')}“.`,
          });

          setTimeout(() => {window.location.reload()}, 200);
        } catch (e) {
          console.error(e);

          messageBoxError({
            contentDe: 'Es ist ein Fehler aufgetreten. Gruppe(n) konnte(n) nicht dupliziert werden.',
            contentEn: 'An error appeared. Could not clone group(s).',
          });
        }
        break;
      case 'delete':
        this.confirmPopup.classList.add('m-popup--is-active');

        const confirmDeleteButton = this.confirmPopup.querySelector('.a-button:last-child') as HTMLLinkElement;

        confirmDeleteButton.addEventListener('click', async (e: Event) => {
          e.preventDefault();

          const requests = [];

          try {
            for (const element of this.selectedElements) {
              const listToDelete = this.wishlistContent.filter((list: any) => list.list_id == element.id)[0];

              for (const section of listToDelete.sections) {
                requests.push(listActionStandard(createDeleteListFormData(section.section_id)));
              }

              requests.push(listActionStandard(createDeleteListFormData(element.id)));
            }

            await Promise.all(requests);

            messageBoxSuccess({
              contentDe: 'Gruppe(n) wurde(n) erfolgreich gelöscht.',
              contentEn: 'Successfully deleted group(s).',
            });

            setTimeout(() => {window.location.reload()}, 200);
          } catch (e) {
            console.error(e);

            messageBoxError({
              contentDe: 'Es ist ein Fehler aufgetreten. Gruppe(n) konnte(n) nicht gelöscht werden.',
              contentEn: 'An error appeared. Could not delete group(s).',
            });
          }
        });
        break;
      default:
    }
  }

  async addGroupsToCart(e: Event) {
    e.preventDefault();

    const requests = [];

    document.body.classList.add('loading-bar');

    try {
      let allProductsAmount = 0;
      let addedToCartProductsAmount = 0;
      let configurationInListGlobal = false;
      let unavailableProductInListGlobal = false;
      let accessoriesProducts: { id: any; quantity: any; }[] = [];

      for (const element of this.selectedElements) {
        let groupItemsAmount = 0;
        let configurationInGroup = false;
        let unavailableProductInGroup = false;

        const selectedList = this.wishlistContent.filter((list: any) => list.list_id == element.id)[0];

        const items = selectedList.items;

        items.forEach((item: any) => {
          allProductsAmount += 1;
          groupItemsAmount += 1;

          if (item.hide_price) {
            configurationInGroup = true;
            configurationInListGlobal = true;
          }
          if(!item.purchasable) {
            unavailableProductInGroup = true;
            unavailableProductInListGlobal = true;
          }
          if (item['sku'] != null && item['sku'] != "" && item['with_accessories'] != null && item['with_accessories'] != "") {
            accessoriesProducts.push({
              'id' : item['with_accessories'],
              'quantity' : item.quantity
            });
          }
        });

        if (!configurationInGroup && !unavailableProductInGroup) {
          addedToCartProductsAmount += groupItemsAmount;
          requests.push(listActionStandard(createAddToCartListFormData(element.id)));
        } else {
          for (const item of items) {
            if (!item.hide_price && item.purchasable) {
              addedToCartProductsAmount += 1;
              const addItemToCartFormdata = createAddToCartListFormData(element.id);

              addItemToCartFormdata.append(`purchasables[${item.wishlist_id}]`, item.wishlist_id);
              addItemToCartFormdata.append(`purchasables[${item.wishlist_id}][qty]`, item.quantity);

              requests.push(listActionStandard(addItemToCartFormdata));
            }
          };
        }
        for (const section of selectedList.sections) {
          let sectionItemsAmount = 0;
          let configurationInSection = false;
          let unavailableProductInSection = false;

          section.items.forEach((item: any) => {
            allProductsAmount += 1;
            sectionItemsAmount += 1;

            if (item.hide_price) {
              configurationInSection = true;
              configurationInListGlobal = true;
            }
            if (!item.purchasable) {
              unavailableProductInSection = true;
              unavailableProductInListGlobal = true;
            }
            if (item['sku'] != null && item['sku'] != "" && item['with_accessories'] != null && item['with_accessories'] != "") {
              accessoriesProducts.push({
                'id' : item['with_accessories'],
                'quantity' : item.quantity
              });
            }
          });

          if (!configurationInSection && !unavailableProductInSection) {
            addedToCartProductsAmount += sectionItemsAmount;
            requests.push(listActionStandard(createAddToCartListFormData(section.section_id)));
          } else {
            for (const item of section.items) {
              if (!item.hide_price && item.purchasable) {
                addedToCartProductsAmount += 1;
                const addItemToCartFormdata = createAddToCartListFormData(section.section_id);

                addItemToCartFormdata.append(`purchasables[${item.wishlist_id}]`, item.wishlist_id);
                addItemToCartFormdata.append(`purchasables[${item.wishlist_id}][qty]`, item.quantity);

                requests.push(listActionStandard(addItemToCartFormdata));
              }
            };
          }
        }
      }

      await Promise.all(requests);

      if (accessoriesProducts.length > 0) {
        const requiredAccessoriesEvent = new CustomEvent('formpopup:open', {
          detail: {
            form: 'required-accessories',
            type: 'form',
            size: 'large--py-0',
            target: 'form-popup',
            products: JSON.stringify(accessoriesProducts)
          },
        } as any);

        document.dispatchEvent(requiredAccessoriesEvent);

        const handleAfterModal = () => {
          if (configurationInListGlobal || unavailableProductInListGlobal) {
            messageBoxSuccess({
              contentDe: `${addedToCartProductsAmount} von ${allProductsAmount} Produkten wurden erfolgreich zum Warenkorb hinzugefügt.`,
              contentEn: `${addedToCartProductsAmount} of ${allProductsAmount} products successfully added to cart.`,
            });
          } else {
            messageBoxSuccess({
              contentDe: 'Gruppe(n) wurde(n) erfolgreich zum Warenkorb hinzugefügt.',
              contentEn: 'Successfully added group(s) to cart.',
            });
          }

          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { showCart: true },
          } as any));

          setTimeout(() => {
            openOptionalModal(configurationInListGlobal, unavailableProductInListGlobal);
          }, 4500);

          window.removeEventListener('required-accessories:close', handleAfterModal);
        };

        window.addEventListener('required-accessories:close', handleAfterModal);
      } else {
        messageBoxSuccess({
          contentDe: 'Gruppe(n) wurde(n) erfolgreich zum Warenkorb hinzugefügt.',
          contentEn: 'Successfully added group(s) to cart.',
        });

        setTimeout(() => {
          openOptionalModal(configurationInListGlobal, unavailableProductInListGlobal);
        }, 4500);
      }
      const apiBaseUrl = document.body.dataset.apiBaseUrl;
      const cartResponse = await fetch(`${apiBaseUrl}api/v1/cart`, {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
        },
      });
      const cart = await cartResponse.json();

      this.cartIconHeader = document.querySelector('.l-header__actions-button-cart .js-l-header__cart-icon');
      this.cartIconHeaderInner = this.cartIconHeader?.querySelector('tspan');

      if (this.cartIconHeader) {
        this.cartIconHeader.style.display = 'block';
      }

      if (this.cartIconHeaderInner) {
        let sum = 0;
        let alreadyCounted: any[] = [];
        cart.lineItems.forEach((lineItem: any) => {
          if (!lineItem.options?._configuratorId || lineItem.options?._configuratorMainItem === 'yes') {
            sum += lineItem.qty;
          }
          alreadyCounted.push(lineItem.options?._configuratorId);
        });
        this.cartIconHeaderInner.innerHTML = String(sum);
      }

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { showCart: accessoriesProducts.length > 0 ? false : true },
      } as any));

      document.body.classList.remove('loading-bar');
    } catch (e) {
      console.error(e);

      document.body.classList.remove('loading-bar');

      messageBoxError({
        contentDe: 'Es ist ein Fehler aufgetreten. Gruppe(n) konnte(n) nicht zum Warenkorb hinzugefügt werden.',
        contentEn: 'An error appeared. Could not add group(s) to cart.',
      });
    }
  }

  bindings() {
    if (this.groupTeaserTrigger) {
      this.groupTeaserTrigger.forEach((trigger) => {
        trigger.addEventListener('click', this.handleToggleSelection.bind(this));
      });
    }

    if (this.groupEditorSelectAll) {
      this.groupEditorSelectAll.addEventListener('click', this.handleToggleSelectAll.bind(this));
    }

    if (this.actionButtons) {
      this.actionButtons.forEach((button) => {
        button.addEventListener('click', this.handleActionButton.bind(this));
      });
    }

    if (this.confirmPopup) {
      this.confirmPopup.querySelector('.a-button:first-child')?.addEventListener('click', () => {
        this.confirmPopup.classList.remove('m-popup--is-active');
      });
    }

    if (this.addToCartButtons) {
      this.addToCartButtons.forEach((button) => {
        button.addEventListener('click', this.addGroupsToCart.bind(this));
      });
    }

    window.addEventListener('scroll', () => {
      const newScroll = window.scrollY;

      if (newScroll > this.lastScroll && window.scrollY > 500) {
        this.groupEditor?.classList.remove('visible');
      } else {
        this.groupEditor?.classList.add('visible');
      }

      this.lastScroll = newScroll;
    });
  }

  cache() {
    this.groupTeasers = document.querySelectorAll('.m-wishlist-group-teaser');
    this.groupTeaserTrigger = document.querySelectorAll('.m-wishlist-group-teaser__selection');
    this.groupEditor = document.querySelector('.m-wishlist-group-editor') as HTMLElement;
    this.groupEditorAmount = this.groupEditor?.querySelector('.m-wishlist-group-editor__groups-amount') as HTMLElement;
    this.groupEditorSelectAll = this.groupEditor?.querySelector('.m-wishlist-group-editor__select-button') as HTMLElement;
    this.actionButtons = this.groupEditor?.querySelectorAll('.a-wishlist-button');
    this.confirmPopup = document.querySelector('.m-wishlist-popup.m-confirm-popup') as HTMLElement;
    this.addToCartButtons = this.groupEditor?.querySelectorAll('.js-add-to-cart');
    this.cartIconHeader = document.querySelector('.l-header__actions-button-cart .js-l-header__cart-icon');
    this.cartIconHeaderInner = this.cartIconHeader?.querySelector('tspan');
  }

  init() {
    this.cache();
    this.bindings();
  }
}

export default GroupEditor;
